/**
 * ч.1374 — кнопка «ціна …» поруч із «+ додати товар»: три ознаки ПДВ документа як у 1С
 * («Враховувати ПДВ», «Сума включає ПДВ», «Автоматичний розрахунок ПДВ»). Підпис кнопки
 * показує стан без відкриття вікна; вікно — у порталі (`useAnchoredPopup`), як інші випадні
 * вікна полів. Для організації-неплатника ПДВ кнопки немає: у неї документи без ПДВ завжди.
 */
import { useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { POPUP_Z, useAnchoredPopup } from '@/shared/hooks/useAnchoredPopup';
import { VAT_FLAG_TEXTS, type DocVatFlags, vatFlagsLabel } from '@/shared/utils/vatFlags';

export default function VatFlagsButton({ value, onChange, disabled, isVatPayer }: {
  value: DocVatFlags;
  onChange: (next: DocVatFlags) => void;
  disabled?: boolean;
  isVatPayer: boolean;
}) {
  const [open, setOpen] = useState(false);
  const btnRef = useRef<HTMLButtonElement>(null);
  const popRef = useRef<HTMLDivElement>(null);
  const pop = useAnchoredPopup(open, btnRef, popRef, () => setOpen(false), { estHeight: 220, gap: 4 });
  if (!isVatPayer) return null;

  const toggle = (key: keyof DocVatFlags) => {
    const next = { ...value, [key]: !value[key] };
    // Без ПДВ у документі «сума включає ПДВ» і авторозрахунок сенсу не мають — лишаємо як є,
    // але в рядках ПДВ стане 0 (перерахунок робить редактор).
    onChange(next);
  };

  return (
    <>
      <button type="button" ref={btnRef} onClick={() => setOpen((v) => !v)} disabled={disabled}
        aria-haspopup="dialog" aria-expanded={open}
        title="Ціни та ПДВ у документі: враховувати ПДВ, сума включає ПДВ, автоматичний розрахунок ПДВ (умовчання — з картки організації)">
        {vatFlagsLabel(value)} ▾
      </button>
      {open && createPortal(
        <div ref={popRef} role="dialog" aria-label="Ціни та ПДВ у документі" style={{
          ...pop.style, zIndex: POPUP_Z, background: 'var(--bg-panel)', border: '1px solid var(--border)',
          borderRadius: 8, boxShadow: 'var(--shadow-lg)', padding: '0.6rem 0.8rem', minWidth: 300,
          color: 'var(--text)', fontSize: '0.9rem', textAlign: 'left',
        }}>
          <div style={{ fontWeight: 600, marginBottom: '0.4rem' }}>Податки у документі</div>
          {(Object.keys(VAT_FLAG_TEXTS) as (keyof DocVatFlags)[]).map((key) => (
            <label key={key} title={VAT_FLAG_TEXTS[key].hint}
              style={{ display: 'flex', alignItems: 'flex-start', gap: '0.5rem', padding: '0.2rem 0',
                cursor: 'pointer', opacity: key !== 'vat_considered' && !value.vat_considered ? 0.5 : 1 }}>
              <input type="checkbox" checked={value[key]} onChange={() => toggle(key)}
                disabled={disabled || (key !== 'vat_considered' && !value.vat_considered)}
                style={{ width: 'auto', marginTop: '0.2rem' }} />
              <span>
                {VAT_FLAG_TEXTS[key].label}
                <small style={{ display: 'block', color: 'var(--muted)', lineHeight: 1.25 }}>
                  {VAT_FLAG_TEXTS[key].hint}
                </small>
              </span>
            </label>
          ))}
          <small style={{ display: 'block', color: 'var(--muted)', marginTop: '0.4rem' }}>
            Умовчання для нових документів — у картці організації, поруч із «Платник ПДВ».
          </small>
        </div>,
        document.body,
      )}
    </>
  );
}
